// Where Is It: hidden objects in a busy flat-color scene. Find five; one riddle object pays gems.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import { pick, shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { SCENES, drawObj, type Obj } from './scenes';

function mount(host: HTMLElement, ctx: GameContext) {
  const scene = pick(ctx.rng, SCENES);
  // jitter positions a little per visit; keep every object fully inside the scene
  const objs: Obj[] = scene.objects.map(o => ({ ...o, x: Math.min(0.95, Math.max(0.05, o.x + (ctx.rng() - 0.5) * 0.06)), y: Math.min(0.96, Math.max(0.08, o.y + (ctx.rng() - 0.5) * 0.06)) }));
  // five targets with distinct names, never the riddle object
  const candidates = shuffle(ctx.rng, objs.map((_, i) => i).filter(i => i !== scene.riddle.index));
  const targets: number[] = [];
  const names = new Set<string>();
  for (const i of candidates) { if (targets.length >= 5) break; if (names.has(objs[i].kind + objs[i].c)) continue; names.add(objs[i].kind + objs[i].c); targets.push(i); }
  const found = new Set<number>();
  let riddleFound = false;
  let taps = 0;
  let lastProgress = performance.now();
  let pulse = 0;            // seconds left of pulsing the next target
  let ripple: { x: number; y: number; t: number } | null = null;
  let finished = false;
  const t0 = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('strong', null, scene.name), h('button.btn.small.ghost', { onclick: () => { pulse = 2.5; } }, 'Hint'));
  const list = h('div.row', { style: { justifyContent: 'center', gap: '6px' } });
  const stage = h('div');
  const riddle = h('p.center.muted.small', null, `Bonus riddle: find ${scene.riddle.text}.`);
  host.append(head, list, stage, riddle);

  const icons: HTMLCanvasElement[] = targets.map(i => {
    const cv = document.createElement('canvas');
    const dpr = window.devicePixelRatio || 1;
    cv.width = 56 * dpr; cv.height = 56 * dpr; cv.style.width = '56px'; cv.style.height = '56px';
    cv.style.borderRadius = '10px'; cv.style.background = 'var(--surface)'; cv.style.border = '2px solid var(--line)';
    const c = cv.getContext('2d')!; c.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawObj(c, objs[i], 28, 28, 34);
    list.append(cv);
    return cv;
  });

  const S = makeSurface(stage, { aspect: 1.05, onResize: () => {} });

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      if (finished) return;
      taps++;
      let best = -1, bd = 1e9;
      objs.forEach((o, i) => {
        const d = Math.hypot(p.x - o.x * S.w, p.y - o.y * S.h);
        const radius = Math.max(28, o.s * 0.6);
        if (d < radius && d < bd) { bd = d; best = i; }
      });
      if (best >= 0 && targets.includes(best) && !found.has(best)) {
        found.add(best);
        const ic = icons[targets.indexOf(best)];
        ic.style.opacity = '.4'; ic.style.borderColor = 'var(--good)';
        ctx.sound('ding');
        ctx.say(`You found ${objs[best].name}!`);
        lastProgress = performance.now(); pulse = 0;
        if (found.size >= targets.length) finish();
      } else if (best === scene.riddle.index && !riddleFound) {
        riddleFound = true;
        ctx.sound('chime');
        ctx.award(10, 'riddle object');
        ctx.say(`Yes! ${objs[best].name[0].toUpperCase() + objs[best].name.slice(1)} is ${scene.riddle.text}.`);
        riddle.textContent = 'Riddle solved!';
        riddle.style.color = 'var(--good)';
      } else {
        ripple = { x: p.x, y: p.y, t: 0.5 };
        ctx.sound('tick');
        if (best >= 0) ctx.say(`That is ${objs[best].name}. Keep looking!`);
      }
    },
  });

  function finish() {
    finished = true;
    if (taps === targets.length) ctx.charm('charm.eagle');
    ctx.say('All found! Sharp eyes.');
    setTimeout(() => ctx.end({ attempts: taps, correct: targets.length, perfect: taps === targets.length, durationMs: performance.now() - t0, notes: riddleFound ? ['Riddle object found'] : [] }), 800);
  }

  function update(dt: number) {
    if (ripple) { ripple.t -= dt; if (ripple.t <= 0) ripple = null; }
    if (pulse > 0) pulse -= dt;
    else if (!finished && performance.now() - lastProgress > 12000) { pulse = 2.5; lastProgress = performance.now(); }
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = scene.sky; c.fillRect(0, 0, W, H);
    c.fillStyle = scene.ground; c.fillRect(0, H * scene.horizon, W, H * (1 - scene.horizon));
    const order = objs.map((o, i) => i).sort((a, b) => objs[a].y - objs[b].y);
    const next = targets.find(i => !found.has(i));
    for (const i of order) {
      const o = objs[i];
      const x = o.x * W, y = o.y * H;
      if (found.has(i)) { c.globalAlpha = 0.35; }
      drawObj(c, o, x, y, o.s * (W / 412));
      c.globalAlpha = 1;
      if (found.has(i)) { c.strokeStyle = '#5fbf6a'; c.lineWidth = 3; c.beginPath(); c.moveTo(x - 10, y); c.lineTo(x - 3, y + 8); c.lineTo(x + 12, y - 8); c.stroke(); }
      if (pulse > 0 && i === next) { c.strokeStyle = '#f5c542'; c.lineWidth = 3; c.beginPath(); c.arc(x, y, o.s * 0.6 + 6 + Math.sin(performance.now() / 120) * 4, 0, 7); c.stroke(); }
    }
    if (ripple) { c.strokeStyle = 'rgba(42,42,46,.5)'; c.lineWidth = 2; c.beginPath(); c.arc(ripple.x, ripple.y, (0.5 - ripple.t) * 60 + 6, 0, 7); c.stroke(); }
  }

  const loop = makeLoop(update, draw);
  loop.start();
  ctx.say('Find the five things shown at the top. Tap them in the scene.');
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'fun.where-is-it',
  title: 'Where Is It',
  blurb: 'A busy scene, five things to find. Spot them all.',
  category: 'fun',
  skills: [],
  levelRange: [1, 10],
  unlock: { gems: 180 },
  mount,
};
export default mod;
